import apiClient from './apiClient';
import type { ClienteLocaisPayload, ClienteLocaisPreview } from '../types/contagemLocais';

interface Job {
  id: number;
  hospital_id: number;
  hospital_nome: string;
  hospital_cliente_codigo?: string; // Código do hospital no cliente
  cliente_id?: number;
  cliente_razao_social?: string;
  cliente_nome_fantasia?: string;
  clientes?: Array<{id: number; razao_social: string; nome_fantasia?: string; codigo: string}>; // Epic 28: Multiple clients
  location_id: number;
  location_nome: string;
  operador_id: number;
  operador_nome: string;
  titulo: string;
  descricao?: string;
  motivo_cancelamento?: string;
  status: 'PENDING' | 'CONFIRMED' | 'IN_PROGRESS' | 'PAUSED' | 'DIVERGENCE' | 'ADJUSTED' | 'COMPLETED' | 'UNDER_REVIEW' | 'APPROVED' | 'REJECTED' | 'SENT' | 'ARCHIVED' | 'CANCELLED';
  previous_status?: string;
  status_changed_by_id?: number;
  status_changed_at?: string;
  data_prevista: string;
  hora_inicio?: string; // Scheduling: hora de início (HH:MM format)
  hora_fim?: string; // Scheduling: hora de fim (HH:MM format)
  duracao?: number; // Calculated: duração em minutos (hora_fim - hora_inicio)
  data_inicio?: string;
  data_fim?: string;
  total_items_counted: number;
  total_quantity_counted: number;
  produto_atribuido?: boolean;
  // Epic 23: M:N relationships
  auditores?: Array<{id: number; nome: string; email: string}>;
  locais_contagem?: Array<{id: number; nome: string}>;
  // Epic LOCAIS-POR-CLIENTE: grouped cliente→locais response (backend Story 6)
  cliente_locais?: ClienteLocaisPreview[];
  // Epic 26 v5.0: Seal tracking and audit fields
  lacre_qtd_coleta_manual?: string;
  lacre_qtd_coleta_vencimento?: string;
  lacre_qtd_coleta_cliente?: string;
  data_cancelamento?: string;
  id_usuario_cancelou?: number;
  usuario_cancelou_nome?: string;
  data_envio_relatorio?: string;
  id_usuario_enviou_relatorio?: number;
  usuario_enviou_relatorio_nome?: string;
  data_envio_saldos?: string;
  envio_saldos_calculado?: string; // Calculado automaticamente: 24h antes da contagem
  // Epic 41: Informações da Contagem (campos read-only populados via Excel)
  ordem_coleta?: string;
  hora_inicio_contagem?: string; // HH:MM format
  hora_termino_contagem?: string; // HH:MM format
}

interface JobCreate {
  hospital_id: number;
  location_id: number;
  operador_id: number;
  cliente_id: number;  // OBRIGATÓRIO - Epic 19
  cliente_ids?: number[]; // Epic 28: Multiple clients (preferred over cliente_id)
  titulo: string;
  descricao?: string;
  data_prevista: string;
  hora_inicio?: string; // Scheduling: hora de início (HH:MM format)
  hora_fim?: string; // Scheduling: hora de fim (HH:MM format)
  product_ids?: number[];
  auditor_ids?: number[]; // Epic 23: M:N auditores
  local_ids?: number[]; // Epic 23: M:N locais de contagem (legacy flat list)
  // Epic LOCAIS-POR-CLIENTE: per-cliente mapping of locais (preferred over local_ids).
  // `null`/`undefined` = auto-seed on backend; `[]` = clear all.
  cliente_locais?: ClienteLocaisPayload[] | null;
}

interface JobFilters {
  status?: string;
  hospital_id?: number;
  search?: string;
  page?: number;
  page_size?: number;
  data_inicio?: string;
  data_fim?: string;
  cliente_id?: number;
  // Story 2.60: listagem da lixeira (MASTER only — backend valida)
  include_deleted?: boolean;
}

// Epic XX: Performance Optimization - Resposta paginada
interface PaginatedJobsResponse {
  items: Job[];
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}

const jobService = {
  getJobs: async (filters: JobFilters = {}): Promise<PaginatedJobsResponse> => {
    const params: Record<string, string> = {};
    if (filters.status) params.status = filters.status;
    if (filters.hospital_id) params.hospital_id = filters.hospital_id.toString();
    if (filters.search) params.search = filters.search;
    if (filters.page) params.page = filters.page.toString();
    if (filters.page_size) params.page_size = filters.page_size.toString();
    if (filters.data_inicio) params.data_inicio = filters.data_inicio;
    if (filters.data_fim) params.data_fim = filters.data_fim;
    if (filters.cliente_id) params.cliente_id = filters.cliente_id.toString();
    // Story 2.60: listagem de excluidas (MASTER only — backend valida)
    if (filters.include_deleted) params.include_deleted = 'true';

    const response = await apiClient.get<PaginatedJobsResponse>('/jobs', { params });
    return response.data;
  },

  getJob: async (id: number): Promise<Job> => {
    const response = await apiClient.get(`/jobs/${id}`);
    return response.data;
  },

  createJob: async (data: JobCreate): Promise<Job> => {
    const response = await apiClient.post('/jobs', data);
    return response.data;
  },

  updateJob: async (id: number, data: Partial<JobCreate>): Promise<Job> => {
    const response = await apiClient.put(`/jobs/${id}`, data);
    return response.data;
  },

  deleteJob: async (id: number): Promise<void> => {
    await apiClient.delete(`/jobs/${id}`);
  },

  // Story 2.60: restaurar contagem soft-deletada (MASTER only — backend valida)
  restoreJob: async (id: number): Promise<{ id: number; titulo: string; message: string }> => {
    const response = await apiClient.post(`/jobs/${id}/restore`);
    return response.data;
  },

  // Epic 14 US-14.4: Workflow Transition Endpoints
  confirmJob: async (id: number): Promise<Job> => {
    const response = await apiClient.post(`/jobs/${id}/confirm`);
    return response.data;
  },

  startJob: async (id: number): Promise<Job> => {
    const response = await apiClient.post(`/jobs/${id}/start`);
    return response.data;
  },

  pauseJob: async (id: number): Promise<Job> => {
    const response = await apiClient.post(`/jobs/${id}/pause`);
    return response.data;
  },

  resumeJob: async (id: number): Promise<Job> => {
    const response = await apiClient.post(`/jobs/${id}/resume`);
    return response.data;
  },

  completeJob: async (id: number): Promise<Job> => {
    const response = await apiClient.post(`/jobs/${id}/complete`);
    return response.data;
  },

  reviewJob: async (id: number): Promise<Job> => {
    const response = await apiClient.post(`/jobs/${id}/review`);
    return response.data;
  },

  approveJob: async (id: number): Promise<Job> => {
    const response = await apiClient.post(`/jobs/${id}/approve`);
    return response.data;
  },

  rejectJob: async (id: number): Promise<Job> => {
    const response = await apiClient.post(`/jobs/${id}/reject`);
    return response.data;
  },

  sendJob: async (id: number): Promise<Job> => {
    const response = await apiClient.post(`/jobs/${id}/send`);
    return response.data;
  },

  archiveJob: async (id: number): Promise<Job> => {
    const response = await apiClient.post(`/jobs/${id}/archive`);
    return response.data;
  },

  cancelJob: async (id: number): Promise<Job> => {
    const response = await apiClient.post(`/jobs/${id}/cancel`);
    return response.data;
  },

  // Reschedule job (drag & drop no Gantt Chart)
  rescheduleJob: async (id: number, hora_inicio: string, hora_fim: string): Promise<Job> => {
    const response = await apiClient.patch(`/jobs/${id}/reschedule`, {
      hora_inicio,
      hora_fim
    });
    return response.data;
  },

  getCounts: async (jobId: number) => {
    const response = await apiClient.get(`/jobs/${jobId}/counts`);
    return response.data;
  },

  // Get expected items (itens no sistema) for barcode simulator
  // Uses /contagens/{job_id}/itens-esperados endpoint
  getExpectedItems: async (jobId: number, page: number = 1, pageSize: number = 200) => {
    const response = await apiClient.get(`/contagens/${jobId}/itens-esperados`, {
      params: { page, page_size: pageSize }
    });
    return response.data;
  },

  uploadCounts: async (jobId: number, counts: any[]) => {
    const response = await apiClient.post(`/jobs/${jobId}/counts/batch`, { counts });
    return response.data;
  },

  downloadJobData: async (jobId: number) => {
    const response = await apiClient.get(`/jobs/${jobId}/download`);
    return response.data;
  },

  // Epic Reorganização Abas: Get Locais Retornados (do Excel)
  getLocaisRetorno: async (jobId: number) => {
    const response = await apiClient.get(`/jobs/${jobId}/locais-retorno`);
    return response.data;
  },

  // Todos os locais de contagem disponíveis para um hospital
  getLocaisContagem: async (hospitalId: number): Promise<{id: number; nome: string; responsavel: string}[]> => {
    if (!hospitalId) return [];
    const response = await apiClient.get(`/estabelecimentos/${hospitalId}/locais-contagem`);
    return response.data.map((l: any) => ({ id: l.id, nome: l.local || l.nome, responsavel: l.responsavel || '' }));
  },

  // Preview locais de contagem auto-computados para hospital + clientes
  getPreviewLocais: async (hospitalId: number, clienteIds: number[]): Promise<{id: number; nome: string; responsavel: string}[]> => {
    if (!hospitalId || clienteIds.length === 0) return [];
    const response = await apiClient.get('/jobs/preview-locais', {
      params: {
        hospital_id: hospitalId,
        cliente_ids: clienteIds.join(','),
      },
    });
    return response.data;
  },

  // Epic LOCAIS-POR-CLIENTE Story 7: preview locais AGRUPADOS por cliente.
  // Returns structure that includes `is_shared` flag and all linked locais per cliente.
  getPreviewLocaisPorCliente: async (
    hospitalId: number,
    clienteIds: number[],
  ): Promise<ClienteLocaisPreview[]> => {
    if (!hospitalId || clienteIds.length === 0) return [];
    const response = await apiClient.get('/jobs/preview-locais-por-cliente', {
      params: {
        hospital_id: hospitalId,
        cliente_ids: clienteIds.join(','),
      },
    });
    return response.data;
  },

  // Helper functions to get related data
  getHospitals: async () => {
    const response = await apiClient.get('/hospitals');
    return response.data;
  },

  getClientes: async () => {
    const response = await apiClient.get('/clientes');
    return response.data;
  },

  getLocations: async (hospitalId: number) => {
    const response = await apiClient.get(`/hospitals/${hospitalId}/locations`);
    return response.data;
  },

  getOperators: async () => {
    const response = await apiClient.get('/users', { params: { role: 'OPERATOR' } });
    return response.data;
  },

  getProducts: async (search: string = '') => {
    const params = search ? { search } : {};
    const response = await apiClient.get('/products', { params });
    return response.data;
  },

  getProdutosDisponiveis: async (clienteId: number): Promise<ProdutoDisponivel[]> => {
    const response = await apiClient.get(`/clientes/${clienteId}/produtos-disponiveis`);
    return response.data;
  },

  uploadExcel: async (file: File, hospital_id: number, location_id: number, operador_id: number, titulo?: string, cliente_id?: number): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);

    const params: Record<string, string> = {
      hospital_id: hospital_id.toString(),
      location_id: location_id.toString(),
      operador_id: operador_id.toString(),
    };
    
    if (titulo) {
      params.titulo = titulo;
    }

    if (cliente_id) {
      params.cliente_id = cliente_id.toString();
    }

    const response = await apiClient.post('/jobs/upload-excel', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      params,
    });
    
    return response.data;
  },

  // Epic 41: Informações da Contagem Excel Import/Export
  importInformacoesExcel: async (jobId: number, file: File): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post(`/jobs/${jobId}/import-informacoes-excel`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  },

  downloadTemplateInformacoes: async (jobId: number): Promise<Blob> => {
    const response = await apiClient.get(`/jobs/${jobId}/download_template_informacoes`, {
      responseType: 'blob',
    });

    return response.data;
  },

  // Epic 14 US-14.5: Format job status for display
  formatJobStatus: (status: string) => {
    const STATUS_CONFIG: Record<string, { label: string; color: string; icon: string }> = {
      PENDING: {
        label: 'Aguardando Início',
        color: 'bg-gray-100 text-gray-800 border-gray-300',
        icon: '⏳'
      },
      CONFIRMED: {
        label: 'Confirmado',
        color: 'bg-blue-100 text-blue-800 border-blue-300',
        icon: '✓'
      },
      IN_PROGRESS: {
        label: 'Em Progresso',
        color: 'bg-yellow-100 text-yellow-800 border-yellow-300',
        icon: '▶'
      },
      PAUSED: {
        label: 'Pausado',
        color: 'bg-orange-100 text-orange-800 border-orange-300',
        icon: '⏸'
      },
      DIVERGENCE: {
        label: 'Divergência',
        color: 'bg-red-100 text-red-800 border-red-300',
        icon: '⚠'
      },
      ADJUSTED: {
        label: 'Ajustado',
        color: 'bg-purple-100 text-purple-800 border-purple-300',
        icon: '🔧'
      },
      COMPLETED: {
        label: 'Completo',
        color: 'bg-green-100 text-green-800 border-green-300',
        icon: '✓'
      },
      UNDER_REVIEW: {
        label: 'Em Conferência',
        color: 'bg-indigo-100 text-indigo-800 border-indigo-300',
        icon: '📋'
      },
      APPROVED: {
        label: 'Aprovado',
        color: 'bg-teal-100 text-teal-800 border-teal-300',
        icon: '✓'
      },
      REJECTED: {
        label: 'Reprovado',
        color: 'bg-red-200 text-red-900 border-red-400',
        icon: '✖'
      },
      SENT: {
        label: 'Enviado',
        color: 'bg-blue-200 text-blue-900 border-blue-400',
        icon: '📤'
      },
      ARCHIVED: {
        label: 'Arquivado',
        color: 'bg-gray-200 text-gray-600 border-gray-400',
        icon: '📦'
      },
      CANCELLED: {
        label: 'Cancelado',
        color: 'bg-red-300 text-red-900 border-red-500',
        icon: '✖'
      }
    };

    return STATUS_CONFIG[status] || {
      label: status,
      color: 'bg-gray-100 text-gray-800 border-gray-300',
      icon: '📋'
    };
  },
};

export interface ProdutoDisponivel {
  id: number;
  codigo: string;
  nome: string;
  unidade: string;
  gtin?: string;
  preco: number;
  codigo_cliente?: string;
  grupo_preco?: string;
  media_consumo?: number;
}

export default jobService;
export type { Job, JobCreate, JobFilters, PaginatedJobsResponse };
